// Έλεγχος GTIN για το Google Merchant Center.
//
// Δεν αρκεί το check-digit: το Google απορρίπτει ως "μη έγκυρο GTIN παγκοσμίως"
// και τα barcodes με πρόθεμα GS1 που είναι δεσμευμένο για εσωτερική χρήση
// (π.χ. 2xxxxx — restricted distribution / ζυγιζόμενα, 02/04, κουπόνια 98x-99x)
// ή με πρόθεμα που δεν έχει εκχωρηθεί σε καμία χώρα/οργανισμό (π.χ. 682).

/** Εκχωρημένα προθέματα GS1 (πρώτα 3 ψηφία του EAN-13), ως εύρη. */
const ASSIGNED_PREFIX_RANGES: Array<[number, number]> = [
  [0, 19],
  [30, 39],
  [60, 139],
  [300, 379],
  [380, 380],
  [383, 383],
  [385, 385],
  [387, 387],
  [389, 389],
  [390, 390],
  [400, 440],
  [450, 459],
  [460, 469],
  [470, 489],
  [490, 499],
  [500, 509],
  [520, 521],
  [528, 531],
  [535, 535],
  [539, 539],
  [540, 549],
  [560, 560],
  [569, 569],
  [570, 579],
  [590, 590],
  [594, 594],
  [599, 599],
  [600, 601],
  [603, 609],
  [611, 611],
  [613, 613],
  [615, 632],
  [640, 649],
  [690, 699],
  [700, 709],
  [729, 729],
  [730, 739],
  [740, 750],
  [754, 755],
  [759, 759],
  [760, 769],
  [770, 771],
  [773, 773],
  [775, 775],
  [777, 777],
  [778, 780],
  [784, 784],
  [786, 786],
  [789, 790],
  [800, 839],
  [840, 849],
  [850, 850],
  [858, 860],
  [865, 865],
  [867, 867],
  [868, 869],
  [870, 879],
  [880, 881],
  [883, 885],
  [888, 888],
  [890, 890],
  [893, 893],
  [896, 896],
  [899, 899],
  [900, 919],
  [930, 939],
  [940, 949],
  [950, 952],
  [955, 955],
  [958, 958],
  [960, 969],
  [977, 979],
];

function hasAssignedPrefix(ean13: string): boolean {
  const p = Number(ean13.slice(0, 3));
  return ASSIGNED_PREFIX_RANGES.some(([lo, hi]) => p >= lo && p <= hi);
}

function checkDigitOk(g: string): boolean {
  const d = g.split("").map(Number).reverse();
  let sum = 0;
  for (let i = 1; i < d.length; i++) sum += d[i]! * (i % 2 === 1 ? 3 : 1);
  return (10 - (sum % 10)) % 10 === d[0];
}

/**
 * Επιστρέφει το GTIN αν είναι έγκυρο παγκοσμίως (σωστό check-digit ΚΑΙ
 * εκχωρημένο, μη-εσωτερικό πρόθεμα GS1), αλλιώς `null`.
 */
export function validGtin(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const g = raw.trim();
  if (!/^\d+$/.test(g)) return null;
  if (![8, 12, 13, 14].includes(g.length)) return null;
  if (/^0+$/.test(g)) return null;
  if (!checkDigitOk(g)) return null;

  if (g.length === 8) {
    // EAN-8: τα προθέματα 0 και 2 είναι εσωτερικής χρήσης.
    return /^[02]/.test(g) ? null : g;
  }

  // Κανονικοποίηση σε EAN-13 για τον έλεγχο προθέματος.
  const ean13 = g.length === 14 ? g.slice(1) : g.padStart(13, "0");
  return hasAssignedPrefix(ean13) ? g : null;
}
